import { useMemo } from 'react';

import type {
  DownloadFilterKey,
  FileEntry,
  FileSortDir,
  FileSortKey,
  SixDetailFilterKey,
} from '@/types/SixDetail.types';

const RECENT_WINDOW_DAYS = 5;
const HOURS_PER_DAY = 24;
const MINUTES_PER_HOUR = 60;
const SECONDS_PER_MINUTE = 60;
const MILLISECONDS_PER_SECOND = 1000;
const RECENT_WINDOW_MS =
  RECENT_WINDOW_DAYS *
  HOURS_PER_DAY *
  MINUTES_PER_HOUR *
  SECONDS_PER_MINUTE *
  MILLISECONDS_PER_SECOND;

interface UseClientSixDetailFileBrowserParams {
  items: FileEntry[];
  searchQuery: string;
  activeFilters: SixDetailFilterKey[];
  downloadFilterKey: DownloadFilterKey;
  sortKey: FileSortKey;
  sortDir: FileSortDir;
  currentUserId: string | undefined;
}

function getSortValue(item: FileEntry, sortKey: FileSortKey): number | string {
  if (sortKey === 'name') {
    return item.name;
  }

  if (sortKey === 'size') {
    return item.rawSizeBytes ?? 0;
  }

  return item.rawLastModified ?? 0;
}

function compareValues(left: number | string, right: number | string): number {
  if (typeof left === 'number' && typeof right === 'number') {
    return left - right;
  }

  return String(left).localeCompare(String(right), undefined, { sensitivity: 'base' });
}

export function useClientSixDetailFileBrowser({
  items,
  searchQuery,
  activeFilters,
  downloadFilterKey,
  sortKey,
  sortDir,
  currentUserId,
}: UseClientSixDetailFileBrowserParams): FileEntry[] {
  return useMemo<FileEntry[]>(() => {
    const normalizedSearch = searchQuery.trim().toLowerCase();
    const now = Date.now();

    const navigationEntry = items.find((item) => item.isNavigationEntry === true);
    const candidateEntries = items.filter((item) => item.isNavigationEntry !== true);

    const passesFilter = (item: FileEntry, filter: SixDetailFilterKey): boolean => {
      switch (filter) {
        case 'favorites':
          return item.kind === 'file' && item.isFavorite === true;
        case 'recent': {
          const lastModifiedMs = item.rawLastModified ?? 0;
          return lastModifiedMs > 0 && now - lastModifiedMs <= RECENT_WINDOW_MS;
        }
        case 'myFilesAndFolders':
          if (item.kind === 'file') {
            return item.uploadedByUserId === currentUserId;
          }

          return item.createdByUserId === currentUserId;
      }
    };

    const filtered = candidateEntries.filter((item) => {
      const matchesSearch =
        normalizedSearch.length === 0
          ? true
          : item.name.toLowerCase().includes(normalizedSearch);

      const matchesFilters = activeFilters.every((filter) => passesFilter(item, filter));
      const matchesDownloadedFilter =
        downloadFilterKey === 'allFiles'
          ? true
          : downloadFilterKey === 'downloaded'
            ? item.kind === 'file' && (item.downloadedByUserName?.length ?? 0) > 0
            : item.kind === 'file' && (item.downloadedByUserName?.length ?? 0) === 0;

      // Temporary stopgap: local search + local filters with AND semantics.
      return matchesSearch && matchesFilters && matchesDownloadedFilter;
    });

    const sorted = [...filtered].sort((left, right) => {
      const baseCompare = compareValues(getSortValue(left, sortKey), getSortValue(right, sortKey));
      return sortDir === 'asc' ? baseCompare : -baseCompare;
    });

    if (navigationEntry === undefined) {
      return sorted;
    }

    return [navigationEntry, ...sorted];
  }, [activeFilters, currentUserId, downloadFilterKey, items, searchQuery, sortDir, sortKey]);
}
